import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from './hooks'
import { fetchPortfolios, setActivePortfolio, invalidatePortfolios, selectActiveRole } from './portfolioSlice'
import type { PortfolioRole, BaseCurrency } from '@/features/assets/api/types'

const STALE_TIME = 5 * 60 * 1000

export function usePortfolio() {
  const dispatch = useAppDispatch()
  const { own, shared, activeId, status, lastFetchedAt } = useAppSelector((s) => s.portfolio)
  const activeRole: PortfolioRole = useAppSelector(selectActiveRole)

  useEffect(() => {
    const isStale = !lastFetchedAt || Date.now() - lastFetchedAt > STALE_TIME
    if (isStale && status !== 'loading') {
      dispatch(fetchPortfolios())
    }
  }, [dispatch, lastFetchedAt, status])

  const activePortfolio = activeId !== null
    ? own.find((p) => p.id === activeId) ?? null
    : null

  const activeShared = activeId !== null
    ? shared.find((s) => s.portfolio_id === activeId) ?? null
    : null

  const activeName = activePortfolio?.name ?? activeShared?.portfolio_name ?? null
  const activeBaseCurrency: BaseCurrency = activePortfolio?.base_currency ?? activeShared?.base_currency ?? 'VND'

  return {
    ownPortfolios: own,
    sharedPortfolios: shared,
    activeId,
    activePortfolio,
    activeShared,
    activeName,
    activeBaseCurrency,
    activeRole,
    canEdit: activeRole === 'owner' || activeRole === 'editor',
    canDelete: activeRole === 'owner',
    isLoading: status === 'loading',
    setActive: (id: number | null) => dispatch(setActivePortfolio(id)),
    refetch: () => dispatch(fetchPortfolios()),
    invalidate: () => dispatch(invalidatePortfolios()),
  }
}
